import { FC, useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Grid, TextField, FormControl, Select, InputLabel, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { SCUIButton } from '../../../../atomic/atoms';
import { SCUIGeneralDrawerHeader } from '../../../../atomic/molecules';

import { CargoManagementQuery } from '../../../../services/query';
import CargoManagementCommand from '../../../../services/command/cargoManagement/CargoManagementCommand.services';
import { CMV, cmvCls } from '../../cargoManagementV2.classes';

export const CargoManagementOrderLimitDrawerContent: FC<any> = ({
    selectedOrderLimit,
    isEdit,
    defaultSelectedCompany,
    companies,
    onClose,
    refresh,
    setLoading,
}) => {
    const [orderLimit, setOrderLimit] = useState<any>(null);
    const [selectedCompany, setSelectedCompany] = useState(0);
    const [county, setCounty] = useState<any>();
    const [counties, setCounties] = useState<any>();
    const [city, setCity] = useState<any>();
    const [cities, setCities] = useState<any>();

    const cityName = cities?.find((cityItem: any) => cityItem.id === city)?.name;
    const countyName = counties?.find((countyItem: any) => countyItem.id === county)?.name;

    const { t } = useTranslation();

    const GetCountyList = async (selectedCity: any) => {
        setLoading(true);
        try {
            let params = {
                ParentAddressEntityIdList: [selectedCity],
                countryId: 1,
            };
            CargoManagementQuery.CountyList(params).then((res: any) => {
                if (res.data?.addressEntities) {
                    let parseData = (res.data?.addressEntities || []).map((item: any) => {
                        return {
                            id: item.addressEntityId,
                            name: item.name,
                        };
                    });
                    setCounties(parseData);
                } else {
                    Swal.fire('Service Error', res.errorMessage, 'error');
                }
                setLoading(false);
            });
        } catch (error: any) {
            setLoading(false);
            Swal.fire('Error', error.toString(), 'error');
        } finally {
            setLoading(false);
        }
    };

    const GetCityList = () => {
        setLoading(true);
        try {
            let params = {
                addressEntityTypeId: 3,
                countryId: 1,
            };
            CargoManagementQuery.CityList(params).then((res: any) => {
                if (res.data?.addressEntities) {
                    let parseData = (res.data.addressEntities || []).map((item: any) => {
                        return {
                            id: item.addressEntityId,
                            name: item.name,
                        };
                    });
                    setCities(parseData);
                } else {
                    Swal.fire('Service Error', res.errorMessage, 'error');
                }
                setLoading(false);
            });
        } catch (error: any) {
            setLoading(false);
            Swal.fire('Error', error.toString(), 'error');
        } finally {
            setLoading(false);
        }
    };

    const AddOrUpdateCargoOrderLimit = () => {
        let params;
        setLoading(true);
        if (isEdit) {
            params = {
                shippingCompanyId: selectedOrderLimit.shippingCompanyId,
                addressEntityId: selectedOrderLimit.addressEntityId,
                orderLimit: +orderLimit,
                senderLocation: 'string',
            };
        } else {
            // CREATE
            params = {
                shippingCompanyId: selectedCompany || defaultSelectedCompany,
                addressEntityId: county || city,
                orderLimit: +orderLimit,
                senderLocation: 'string',
            };
        }

        try {
            CargoManagementCommand.UpdateCargoCompanyPriorityCity(params)
                .then(() => {
                    setCity(null);
                    setCounty(null);
                    setOrderLimit(null);
                    setCounties([]);
                    setSelectedCompany(defaultSelectedCompany);
                    Swal.fire(isEdit ? t('general.updated') : t('general.added'), ' ', 'success');
                    refresh();
                    onClose();
                })
                .catch(() => {
                    setLoading(false);
                    Swal.fire(t('general.error'), ' ', 'error');
                });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        GetCityList();
    }, []);

    useEffect(() => {
        if (cities && selectedOrderLimit) {
            setOrderLimit(selectedOrderLimit?.orderLimit);
            setCity(cities?.find((cityItem: any) => cityItem.name === selectedOrderLimit?.cityName)?.id);
        }
    }, [selectedOrderLimit, cities]);

    useEffect(() => {
        if (isEdit && cities) {
            GetCountyList(cities?.find((cityItem: any) => cityItem.name === selectedOrderLimit?.cityName)?.id);
        }
    }, [isEdit, cities]);

    useEffect(() => {
        if (isEdit && counties) {
            setCounty(counties?.find((countyItem: any) => countyItem.name === selectedOrderLimit?.countyName)?.id);
        }
    }, [isEdit, counties]);

    useEffect(() => {
        if (selectedOrderLimit?.shippingCompanyId) {
            setSelectedCompany(selectedOrderLimit?.shippingCompanyId);
        }
    }, [selectedOrderLimit?.shippingCompanyId]);

    return (
        <Grid className={cmvCls(CMV.drawer, CMV.drawerBody)} sx={{ padding: '16px' }}>
            <Grid container>
                <SCUIGeneralDrawerHeader
                    header={isEdit ? t('cargoManagement.orderLimitUpdate') : t('cargoManagement.orderLimitInsert')}
                    onClose={onClose}
                />
                <Grid
                    className={CMV.drawerForm}
                    sx={{ padding: '16px' }}
                    container
                    size={{
                        xs: 12,
                        md: 12,
                        lg: 12,
                        xl: 12,
                    }}
                >
                    <FormControl fullWidth sx={{ margin: '10px' }} className={CMV.fieldDrawerCompany}>
                        <Select
                            id="parentMenuLabel"
                            className={cmvCls(CMV.input, CMV.inputSelect, CMV.fieldDrawerCompany)}
                            value={selectedCompany || defaultSelectedCompany}
                            onChange={(e: any) => {
                                setSelectedCompany(e.target.value);
                            }}
                            fullWidth
                            disabled={isEdit || defaultSelectedCompany !== 0}
                            renderValue={() => {
                                if (!isEdit && selectedCompany === 0 && defaultSelectedCompany === 0) {
                                    return (
                                        <em style={{ color: 'rgba(0, 0, 0, 0.54)' }}>
                                            {t('cargoManagement.selectCargoCompany')}
                                        </em>
                                    );
                                }

                                return companies?.find(
                                    (companyItem: any) =>
                                        companyItem.id ===
                                        (defaultSelectedCompany !== 0 ? defaultSelectedCompany : selectedCompany)
                                )?.name;
                            }}
                        >
                            {(companies?.slice(1) || []).map((companyItem: any) => (
                                <MenuItem key={companyItem.id} value={companyItem.id}>
                                    {companyItem.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth sx={{ margin: '10px' }} className={CMV.fieldDrawerCity}>
                        <InputLabel id="labelApp">{isEdit ? cityName : t('cargoManagement.selectCity')}</InputLabel>
                        <Select
                            labelId="labelApp"
                            label={t('cargoManagement.selectCity')}
                            id="parentMenuLabel"
                            className={cmvCls(CMV.input, CMV.inputSelect, CMV.fieldDrawerCity)}
                            value={
                                isEdit
                                    ? cities?.find((cityItem: any) => cityItem.name === selectedOrderLimit?.cityName)
                                          ?.id
                                    : city
                            }
                            onChange={(e: any) => {
                                setCity(e.target.value);
                                GetCountyList(e.target.value);
                            }}
                            fullWidth
                            disabled={isEdit || (!isEdit && defaultSelectedCompany === 0 && selectedCompany === 0)}
                        >
                            {(cities || []).map((cityItem: any) => (
                                <MenuItem key={cityItem.id} value={cityItem.id}>
                                    {cityItem.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth sx={{ margin: '10px' }} className={CMV.fieldDrawerCounty}>
                        <InputLabel id="labelApp">{isEdit ? countyName : t('cargoManagement.selectCounty')}</InputLabel>
                        <Select
                            labelId="labelApp"
                            label={t('cargoManagement.selectCounty')}
                            id="parentMenuLabel"
                            className={cmvCls(CMV.input, CMV.inputSelect, CMV.fieldDrawerCounty)}
                            value={isEdit ? countyName : county}
                            onChange={(e: any) => setCounty(e.target.value)}
                            fullWidth
                            disabled={isEdit || !city}
                        >
                            {(counties || []).map((countyItem: any) => (
                                <MenuItem key={countyItem.id} value={countyItem.id}>
                                    {countyItem.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth sx={{ margin: '10px' }} className={CMV.fieldDrawerOrderLimit}>
                        <TextField
                            id="orderLimitValue"
                            label={t('cargoManagement.orderLimitValue')}
                            defaultValue={selectedOrderLimit?.orderLimit}
                            slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '[0-9]*', min: 1 } }}
                            sx={{ width: '100%' }}
                            value={orderLimit}
                            className={cmvCls(CMV.input, CMV.inputTextfield, CMV.fieldDrawerOrderLimit)}
                            onChange={(event) => {
                                const newValue = event.target.value.replace(/[^0-9]/g, '');
                                setOrderLimit(newValue);
                            }}
                        />
                    </FormControl>
                </Grid>
                <Grid
                    className={CMV.drawerActions}
                    sx={{
                        width: '50%',
                        marginBottom: '20px',
                        marginLeft: 'auto',
                    }}
                >
                    <SCUIButton
                        text={isEdit ? t('general.update') : t('general.add')}
                        type="primary"
                        sizeType="default"
                        buttonType="submit"
                        size={'s'}
                        className={cmvCls(CMV.input, CMV.inputButton, CMV.btnDrawerSubmit)}
                        disabled={
                            (defaultSelectedCompany === 0 && !selectedCompany) ||
                            !city ||
                            !county ||
                            !orderLimit ||
                            Number(orderLimit) === 0
                        }
                        fullwidth
                        onClick={AddOrUpdateCargoOrderLimit}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
};
